import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { ToySprite } from './ToySprite';
import { StyledButton } from './StyledButton';
import { getVisualForToy, getRarityColor } from '../config/toys';
import type { ToyConfig } from '../types';

type ResultKind = 'success' | 'failure' | 'nearMiss';

export class ResultOverlay extends Phaser.GameObjects.Container {
  private targetToy: ToyConfig;
  private newUnlock: boolean;
  private continueCallback: () => void;
  private retryCallback: () => void;

  constructor(
    scene: Phaser.Scene,
    resultType: ResultKind,
    toy: ToyConfig,
    isNew: boolean,
    onContinue: () => void,
    onRetry: () => void
  ) {
    super(scene, 640, 360);
    this.targetToy = toy;
    this.newUnlock = isNew;
    this.continueCallback = onContinue;
    this.retryCallback = onRetry;

    this.setDepth(100);

    this.addOverlay();

    if (resultType === 'success') {
      this.createSuccessReveal();
    } else if (resultType === 'nearMiss') {
      this.createNearMiss();
    } else {
      this.createFailure();
    }

    scene.add.existing(this);
  }

  private addOverlay(): void {
    const overlay = this.scene.add.rectangle(0, 0, 1280, 720, 0x000000, 0.45);
    overlay.setInteractive();
    overlay.on('pointerdown', () => {});
    this.add(overlay);
  }

  private createSuccessReveal(): void {
    this.createSunburstBackground();
    this.createConfetti();
    this.createTopStatusBar();

    // "NEW TOY!" banner
    const banner = this.createBanner('NEW TOY!', DESIGN.colors.primary, -220);
    this.add(banner);

    // Main toy reveal card
    const card = this.createToyCard(120, 0xffdad8, 0);
    this.add(card);

    // Rarity badge
    const badgeColor = getRarityColor(this.targetToy.rarity);
    const badge = this.createBadge(this.targetToy.rarity.toUpperCase(), badgeColor, 170, -150);
    this.add(badge);

    // Details card
    const detailsCard = this.createDetailsCard(
      this.targetToy.name,
      this.newUnlock ? 'Collection added!' : 'Already in collection',
      this.targetToy.description ?? this.defaultDescription(),
      180,
      0xffffff
    );
    this.add(detailsCard);

    // Action buttons
    const buttons = this.createTwoButtons(
      { label: 'CONTINUE', icon: '▶', color: DESIGN.colors.primary, onClick: () => this.continueCallback() },
      { label: 'PLAY AGAIN', icon: '🎮', color: DESIGN.colors.secondaryContainer, textColor: DESIGN.colors.secondary, onClick: () => this.retryCallback() }
    );
    this.add(buttons);
  }

  private createNearMiss(): void {
    this.createCabinetBackground();
    this.createTopStatusBar();
    this.createEmptyClaw(-60);

    const content = this.scene.add.container(0, 80);

    const shrug = this.scene.add.text(0, -40, '¯\\_(ツ)_/¯', {
      fontSize: '56px',
      color: DESIGN.colors.onSurface,
    }).setOrigin(0.5);
    content.add(shrug);

    this.scene.tweens.add({
      targets: shrug,
      y: -50,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    content.add(this.scene.add.text(0, 30, 'Almost got it!', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '32px',
      color: DESIGN.colors.nearMiss,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    content.add(this.scene.add.text(0, 75, 'That one was slippery! Give it another shot?', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5));

    content.add(this.scene.add.text(0, 120, '⭐ Don\'t give up! You\'re getting closer.', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5));

    this.add(content);

    // Slipping toy animation
    this.createSlippingToy();

    const retryBtn = this.createPrimaryButton('RETRY', '↻', 0, 240, () => this.retryCallback());
    this.add(retryBtn);
  }

  private createFailure(): void {
    this.createCabinetBackground();
    this.createTopStatusBar();
    this.createEmptyClaw(-60);

    const content = this.scene.add.container(0, 80);

    const shrug = this.scene.add.text(0, -40, '¯\\_(ツ)_/¯', {
      fontSize: '56px',
      color: DESIGN.colors.onSurface,
    }).setOrigin(0.5);
    content.add(shrug);

    this.scene.tweens.add({
      targets: shrug,
      y: -50,
      duration: 2500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    content.add(this.scene.add.text(0, 30, 'Missed it!', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '32px',
      color: DESIGN.colors.fail,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    content.add(this.scene.add.text(0, 75, 'The prize slipped away... Better luck next time!', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5));

    content.add(this.scene.add.text(0, 120, '⭐ Don\'t give up! You\'re getting closer.', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5));

    this.add(content);

    const retryBtn = this.createPrimaryButton('TRY AGAIN', '↻', 0, 240, () => this.retryCallback());
    this.add(retryBtn);
  }

  private createSunburstBackground(): void {
    const graphics = this.scene.add.graphics();
    const colors = [0xffe173, 0xffffff];
    for (let i = 0; i < 36; i++) {
      const angle = (i / 36) * Math.PI * 2;
      const nextAngle = ((i + 1) / 36) * Math.PI * 2;
      const color = colors[i % 2];
      const alpha = i % 2 === 0 ? 0.25 : 0.15;
      graphics.fillStyle(color, alpha);
      graphics.beginPath();
      graphics.moveTo(0, 0);
      graphics.arc(0, 0, 900, angle, nextAngle);
      graphics.closePath();
      graphics.fillPath();
    }
    this.add(graphics);

    this.scene.tweens.add({
      targets: graphics,
      rotation: Math.PI * 2,
      duration: 20000,
      repeat: -1,
      ease: 'Linear',
    });
  }

  private createCabinetBackground(): void {
    const casing = this.scene.add.graphics();
    const primary = parseInt(DESIGN.colors.primary.replace('#', '0x'), 16);
    const primaryContainer = parseInt(DESIGN.colors.primaryContainer.replace('#', '0x'), 16);

    casing.fillStyle(primary, 1);
    casing.fillRoundedRect(-220, -300, 440, 600, 32);
    casing.fillStyle(primaryContainer, 1);
    casing.fillRoundedRect(-210, -290, 420, 580, 28);

    const glass = this.scene.add.graphics();
    glass.fillStyle(0xffffff, 0.9);
    glass.fillRoundedRect(-180, -260, 360, 420, 20);

    this.add(casing);
    this.add(glass);
  }

  private createEmptyClaw(y: number): void {
    const claw = this.scene.add.container(0, y);

    const cord = this.scene.add.rectangle(0, -120, 4, 120, 0xaaaaaa);
    claw.add(cord);

    const head = this.scene.add.circle(0, 0, 28, 0xe1e2e8);
    head.setStrokeStyle(2, 0xaaaaaa);
    claw.add(head);

    const prongLeft = this.scene.add.rectangle(-20, 30, 10, 40, 0xe1e2e8);
    prongLeft.setRotation(0.2);
    claw.add(prongLeft);

    const prongRight = this.scene.add.rectangle(20, 30, 10, 40, 0xe1e2e8);
    prongRight.setRotation(-0.2);
    claw.add(prongRight);

    this.scene.tweens.add({
      targets: claw,
      rotation: 0.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add(claw);
  }

  private createSlippingToy(): void {
    const visual = getVisualForToy(this.targetToy.id);
    const toy = new ToySprite(this.scene, 0, 40, visual, 80);
    this.add(toy);

    this.scene.tweens.add({
      targets: toy,
      x: 140,
      y: 240,
      rotation: 0.8,
      alpha: 0,
      duration: 1500,
      ease: 'Cubic.easeIn',
    });
  }

  private createTopStatusBar(): void {
    const bar = this.scene.add.container(0, -330);

    const logoBg = this.scene.add.rectangle(-520, 0, 160, 36, 0xffffff, 0.8);
    bar.add(logoBg);
    bar.add(this.scene.add.text(-520, 0, 'Toybox Arcade', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '16px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    const badge = this.scene.add.rectangle(460, 0, 180, 36, 0xffe173, 0.9);
    bar.add(badge);
    bar.add(this.scene.add.text(460, 0, 'Toys Collected: --', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: '#554500',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    this.add(bar);
  }

  private createBanner(text: string, color: string, y: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, y);

    const bg = this.scene.add.rectangle(0, 0, 280, 56, parseInt(color.replace('#', '0x'), 16));
    bg.setOrigin(0.5);
    bg.setRotation(-0.03);
    container.add(bg);

    container.add(this.scene.add.text(0, 0, text, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    this.scene.tweens.add({
      targets: container,
      scale: { from: 0.5, to: 1 },
      duration: 600,
      ease: 'Back.easeOut',
    });

    return container;
  }

  private createToyCard(size: number, borderColor: number, y: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, y);

    const glow = this.scene.add.graphics();
    glow.fillStyle(0xff7e7e, 0.3);
    glow.fillCircle(0, 0, 160);
    container.add(glow);

    const bg = this.scene.add.rectangle(0, 0, 240, 240, 0xffffff, 1);
    bg.setStrokeStyle(4, borderColor);
    container.add(bg);

    const visual = getVisualForToy(this.targetToy.id);
    const toy = new ToySprite(this.scene, 0, 0, visual, size);
    container.add(toy);

    this.scene.tweens.add({
      targets: toy,
      y: -10,
      rotation: 0.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.scene.tweens.add({
      targets: container,
      scale: { from: 0, to: 1 },
      duration: 500,
      ease: 'Back.easeOut',
    });

    return container;
  }

  private createBadge(text: string, color: string, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, 80, 34, parseInt(color.replace('#', '0x'), 16));
    bg.setRotation(0.2);
    container.add(bg);

    container.add(this.scene.add.text(0, 0, text, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    return container;
  }

  private createDetailsCard(name: string, subtitle: string, description: string, y: number, bgColor: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, y);

    const shadow = this.scene.add.rectangle(6, 6, 460, 130, 0x000000, 0.1);
    container.add(shadow);

    const bg = this.scene.add.rectangle(0, 0, 460, 130, bgColor, 1);
    bg.setStrokeStyle(2, 0xffffff, 0.5);
    container.add(bg);

    container.add(this.scene.add.text(0, -35, name, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '28px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    container.add(this.scene.add.text(0, -5, `📦 ${subtitle}`, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5));

    container.add(this.scene.add.text(0, 30, `"${description}"`, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'italic',
    }).setOrigin(0.5));

    this.scene.tweens.add({
      targets: container,
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 150,
      ease: 'Back.easeOut',
    });

    return container;
  }

  private createTwoButtons(
    primary: { label: string; icon: string; color: string; onClick: () => void },
    secondary: { label: string; icon: string; color: string; textColor?: string; onClick: () => void }
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, 280);

    const primaryBtn = new StyledButton(this.scene, -130, 0, `${primary.icon} ${primary.label}`, {
      width: 220,
      height: 56,
      bgColor: parseInt(primary.color.replace('#', '0x'), 16),
      shadowColor: parseInt(DESIGN.colors.primaryDark.replace('#', '0x'), 16),
      fontSize: '20px',
      onClick: primary.onClick,
    });
    container.add(primaryBtn);

    const secondaryBtn = new StyledButton(this.scene, 130, 0, `${secondary.icon} ${secondary.label}`, {
      width: 220,
      height: 56,
      bgColor: parseInt(secondary.color.replace('#', '0x'), 16),
      shadowColor: parseInt(secondary.color.replace('#', '0x'), 16) - 0x002220,
      textColor: secondary.textColor ?? '#ffffff',
      fontSize: '20px',
      onClick: secondary.onClick,
    });
    container.add(secondaryBtn);

    this.scene.tweens.add({
      targets: container,
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 300,
      ease: 'Back.easeOut',
    });

    return container;
  }

  private createPrimaryButton(label: string, icon: string, x: number, y: number, onClick: () => void): StyledButton {
    const btn = new StyledButton(this.scene, x, y, `${icon} ${label}`, {
      width: 260,
      height: 60,
      bgColor: parseInt(DESIGN.colors.primary.replace('#', '0x'), 16),
      shadowColor: parseInt(DESIGN.colors.primaryDark.replace('#', '0x'), 16),
      fontSize: '22px',
      onClick,
    });

    this.scene.tweens.add({
      targets: btn,
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 300,
      ease: 'Back.easeOut',
    });

    return btn;
  }

  private createConfetti(): void {
    const colors = [0xa43a3d, 0xff7e7e, 0x88f2e0, 0xffe173, 0x006b5f];
    for (let i = 0; i < 40; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const x = (Math.random() - 0.5) * 1200;
      const y = -360 - Math.random() * 200;
      const size = 4 + Math.random() * 8;
      const particle = this.scene.add.rectangle(x, y, size, size, color);
      particle.setRotation(Math.random() * Math.PI);
      this.add(particle);

      this.scene.tweens.add({
        targets: particle,
        y: 360 + Math.random() * 100,
        x: x + (Math.random() - 0.5) * 200,
        rotation: Math.random() * Math.PI * 4,
        alpha: 0,
        duration: 2000 + Math.random() * 3000,
        delay: Math.random() * 1000,
        repeat: -1,
      });
    }
  }

  private defaultDescription(): string {
    const descriptions: Record<string, string> = {
      bear_brown: 'A cuddly friend from the forest.',
      cat_little: 'A tiny kitten with a big heart.',
      rabbit_cloud: 'Soft as a cloud and twice as fluffy.',
      penguin_waddle: 'Waddles straight into your heart.',
      dog_cookie: 'Smells like fresh-baked cookies.',
      duck_bath: 'Ready for a splashy adventure.',
      frog_leaf: 'Hops around on lily pads all day.',
      hamster_seed: 'Always saving snacks for later.',
      fox_crystal: 'Sparkles with magical energy.',
      penguin_ocean: 'A brave explorer of the deep blue.',
      owl_moon: 'Wise and wonderfully soft.',
      deer_stardust: 'Carries the light of the stars.',
      sheep_dream: 'Counting clouds instead of sheep.',
      dragon_cosmic: 'Born from stardust and moonbeams.',
      phoenix_dream: 'A fiery dream that never fades.',
    };
    return descriptions[this.targetToy.id] ?? 'A wonderful new friend!';
  }
}

interface ResultOverlayConfig {
  scene: Phaser.Scene;
  type: ResultKind;
  toy: ToyConfig;
  isNew: boolean;
  onContinue: () => void;
  onRetry: () => void;
}

export function createResultOverlay(config: ResultOverlayConfig): ResultOverlay {
  return new ResultOverlay(config.scene, config.type, config.toy, config.isNew, config.onContinue, config.onRetry);
}
